/**
 * ThreeWayMatchDialog
 *
 * A fullscreen dialog that displays a side-by-side comparison of:
 * 1. Purchase Order line items
 * 2. Proof of Delivery line items
 * 3. Invoice line items
 *
 * Uses the DocumentComparisonLayout for consistent multi-column comparison UI.
 */

import { useMemo } from 'react';
import { useQueries } from '@tanstack/react-query';
import { PackageIcon, TruckIcon } from '@phosphor-icons/react';
import { ArrowLeft } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { ScrollArea } from '@/components/ui/scroll-area';
import {
  Table,
  TableBody,
  TableCell,
  TableFooter,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { DocumentComparisonLayout } from '@/components/document-comparison/DocumentComparisonLayout';
import { DocumentComparisonProvider } from '@/components/document-comparison/DocumentComparisonProvider';
import type { ColumnConfig } from '@/components/document-comparison/columns/types';
import { orpc } from '@/api/rpc/client';
import type { PurchaseOrderLineItem } from '@erp/rpc';

interface LineItemDisplay {
  id: string;
  lineNumber: number;
  description?: string | null;
  quantity?: string | number | null;
  unitPrice?: string | null;
  totalPrice?: string | null;
}

interface ThreeWayMatchDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  invoiceId: string;
  lineItems: LineItemDisplay[];
  currency?: string;
  associatedPOIds: string[];
  isMatched?: boolean;
}

function formatCurrency(value?: string | null, currency = 'USD') {
  if (!value) return '—';
  const numeric = Number(value);
  if (Number.isNaN(numeric)) return value;
  return numeric.toLocaleString(undefined, {
    style: 'currency',
    currency,
    minimumFractionDigits: 2,
  });
}

function formatQuantity(value: string | number | null | undefined): string {
  if (value === null || value === undefined) return '—';
  const numeric = Number(value);
  if (Number.isNaN(numeric)) return String(value);
  const formatted = numeric.toFixed(4);
  return formatted.replace(/\.?0+$/, '');
}

function InvoiceLineItemsPanel({
  lineItems,
  currency,
  isMatched,
}: {
  lineItems: LineItemDisplay[];
  currency?: string;
  isMatched?: boolean;
}) {
  const total = useMemo(() => {
    return lineItems.reduce(
      (sum, item) => sum + (Number(item.totalPrice) || 0),
      0,
    );
  }, [lineItems]);

  return (
    <ScrollArea className="h-full">
      <div className="p-3">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-12 text-center">#</TableHead>
              <TableHead>Description</TableHead>
              <TableHead className="text-right w-20">Qty</TableHead>
              <TableHead className="text-right w-28">Unit Price</TableHead>
              <TableHead className="text-right w-28">Total</TableHead>
              <TableHead className="text-center w-24">Status</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {lineItems.map((item) => (
              <TableRow key={item.id}>
                <TableCell className="text-center text-muted-foreground">
                  {item.lineNumber}
                </TableCell>
                <TableCell
                  className="font-medium max-w-[180px] truncate"
                  title={item.description || 'No description'}
                >
                  {item.description || 'No description'}
                </TableCell>
                <TableCell className="text-right">
                  {formatQuantity(item.quantity)}
                </TableCell>
                <TableCell className="text-right text-muted-foreground">
                  {formatCurrency(item.unitPrice, currency)}
                </TableCell>
                <TableCell className="text-right font-semibold">
                  {formatCurrency(item.totalPrice, currency)}
                </TableCell>
                <TableCell className="text-center">
                  {isMatched ? (
                    <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800">
                      Match
                    </span>
                  ) : (
                    <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-600">
                      Pending
                    </span>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
          <TableFooter>
            <TableRow>
              <TableCell />
              <TableCell className="font-semibold">Total</TableCell>
              <TableCell />
              <TableCell />
              <TableCell className="text-right font-semibold">
                {formatCurrency(String(total), currency)}
              </TableCell>
              <TableCell className="text-center">
                {isMatched ? (
                  <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800">
                    Match
                  </span>
                ) : (
                  <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-600">
                    Pending
                  </span>
                )}
              </TableCell>
            </TableRow>
          </TableFooter>
        </Table>
      </div>
    </ScrollArea>
  );
}

function POLineItemsPanel({
  poIds,
  currency,
  isMatched,
}: {
  poIds: string[];
  currency?: string;
  isMatched?: boolean;
}) {
  const poQueries = useQueries({
    queries: useMemo(
      () =>
        poIds.map((poId) =>
          orpc.accountsPayable.purchaseOrders.get.queryOptions({
            input: { id: poId },
          }),
        ),
      [poIds],
    ),
  });

  const isLoading = poQueries.some((q) => q.isLoading);

  const allLineItems = useMemo(() => {
    const items: Array<PurchaseOrderLineItem & { poDisplayId?: string }> = [];
    poQueries.forEach((query) => {
      const po = query.data?.purchaseOrder;
      const lineItems = query.data?.lineItems;
      if (po && lineItems) {
        const poDisplayId = po.externalId || `PO-${po.internalId}`;
        lineItems.forEach((item) => {
          items.push({ ...item, poDisplayId });
        });
      }
    });
    return items;
  }, [poQueries]);

  const total = useMemo(() => {
    return allLineItems.reduce(
      (sum, item) => sum + (Number(item.totalPrice) || 0),
      0,
    );
  }, [allLineItems]);

  if (poIds.length === 0) {
    return (
      <div className="flex h-full items-center justify-center text-muted-foreground">
        <div className="text-center">
          <PackageIcon className="h-12 w-12 mx-auto mb-3 opacity-30" />
          <p className="text-sm">No associated Purchase Orders</p>
          <p className="text-xs mt-1">
            Link a PO to this invoice to compare line items
          </p>
        </div>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="flex h-full items-center justify-center text-muted-foreground">
        <p className="text-sm">Loading PO line items...</p>
      </div>
    );
  }

  if (allLineItems.length === 0) {
    return (
      <div className="flex h-full items-center justify-center text-muted-foreground">
        <div className="text-center">
          <PackageIcon className="h-12 w-12 mx-auto mb-3 opacity-30" />
          <p className="text-sm">No line items found in associated POs</p>
        </div>
      </div>
    );
  }

  return (
    <ScrollArea className="h-full">
      <div className="p-3">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-12 text-center">#</TableHead>
              <TableHead>Description</TableHead>
              <TableHead className="text-right w-20">Qty</TableHead>
              <TableHead className="text-right w-28">Unit Price</TableHead>
              <TableHead className="text-right w-28">Total</TableHead>
              <TableHead className="text-center w-24">Status</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {allLineItems.map((item, index) => (
              <TableRow key={item.id}>
                <TableCell className="text-center text-muted-foreground">
                  {item.lineNumber ?? index + 1}
                </TableCell>
                <TableCell
                  className="max-w-[180px]"
                  title={item.description || 'No description'}
                >
                  <div className="font-medium truncate">
                    {item.description || 'No description'}
                  </div>
                  {poIds.length > 1 && item.poDisplayId && (
                    <div className="text-xs text-muted-foreground truncate">
                      PO: {item.poDisplayId}
                    </div>
                  )}
                </TableCell>
                <TableCell className="text-right">
                  {formatQuantity(item.quantity)}
                </TableCell>
                <TableCell className="text-right text-muted-foreground">
                  {formatCurrency(
                    item.unitPrice != null ? String(item.unitPrice) : null,
                    currency,
                  )}
                </TableCell>
                <TableCell className="text-right font-semibold">
                  {formatCurrency(
                    item.totalPrice != null ? String(item.totalPrice) : null,
                    currency,
                  )}
                </TableCell>
                <TableCell className="text-center">
                  {isMatched ? (
                    <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800">
                      Match
                    </span>
                  ) : (
                    <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-600">
                      Pending
                    </span>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
          <TableFooter>
            <TableRow>
              <TableCell />
              <TableCell className="font-semibold">Total</TableCell>
              <TableCell />
              <TableCell />
              <TableCell className="text-right font-semibold">
                {formatCurrency(String(total), currency)}
              </TableCell>
              <TableCell className="text-center">
                {isMatched ? (
                  <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800">
                    Match
                  </span>
                ) : (
                  <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-600">
                    Pending
                  </span>
                )}
              </TableCell>
            </TableRow>
          </TableFooter>
        </Table>
      </div>
    </ScrollArea>
  );
}

function ShippingDocsPanel({
  poIds,
  currency,
}: {
  poIds: string[];
  currency?: string;
}) {
  const shippingDocQueries = useQueries({
    queries: useMemo(
      () =>
        poIds.map((poId) =>
          orpc.supplyChainManagement.shippingDocuments.byPurchaseOrder.queryOptions(
            {
              input: { purchaseOrderId: poId },
            },
          ),
        ),
      [poIds],
    ),
  });

  const isLoading = shippingDocQueries.some((q) => q.isLoading);

  interface ShippingDocLineItem {
    id: string;
    internalPurchaseOrderLineItemId?: string | null;
    expectedQuantity?: number | null;
    actualQuantity?: number | null;
    description?: string | null;
  }

  const allLineItems = useMemo(() => {
    const items: ShippingDocLineItem[] = [];
    shippingDocQueries.forEach((query) => {
      const docs = query.data || [];
      docs.forEach((doc) => {
        const lineItems = (doc.lineItems || []) as ShippingDocLineItem[];
        lineItems.forEach((lineItem) => {
          items.push(lineItem);
        });
      });
    });
    return items;
  }, [shippingDocQueries]);

  const totals = useMemo(() => {
    let expectedTotal = 0;
    let actualTotal = 0;
    allLineItems.forEach((item) => {
      expectedTotal += item.expectedQuantity ?? 0;
      actualTotal += item.actualQuantity ?? 0;
    });
    return { expectedTotal, actualTotal };
  }, [allLineItems]);

  if (poIds.length === 0) {
    return (
      <div className="flex h-full items-center justify-center text-muted-foreground">
        <div className="text-center">
          <TruckIcon className="h-12 w-12 mx-auto mb-3 opacity-30" />
          <p className="text-sm">No associated Purchase Orders</p>
          <p className="text-xs mt-1">Link a PO to view proof of delivery</p>
        </div>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="flex h-full items-center justify-center text-muted-foreground">
        <p className="text-sm">Loading shipping documents...</p>
      </div>
    );
  }

  if (allLineItems.length === 0) {
    return (
      <div className="flex h-full items-center justify-center text-muted-foreground">
        <div className="text-center">
          <TruckIcon className="h-12 w-12 mx-auto mb-3 opacity-30" />
          <p className="text-sm">No proof of delivery found</p>
          <p className="text-xs mt-1 text-muted-foreground/70">
            No delivery records for the associated POs
          </p>
        </div>
      </div>
    );
  }

  return (
    <ScrollArea className="h-full">
      <div className="p-3">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-12 text-center">#</TableHead>
              <TableHead>Description</TableHead>
              <TableHead className="text-right w-24">Expected</TableHead>
              <TableHead className="text-right w-24">Actual</TableHead>
              <TableHead className="text-center w-24">Status</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {allLineItems.map((item, index) => {
              const expectedQty = item.expectedQuantity ?? 0;
              const actualQty = item.actualQuantity ?? 0;
              const hasDiscrepancy = actualQty !== expectedQty;

              return (
                <TableRow key={item.id}>
                  <TableCell className="text-center text-muted-foreground">
                    {index + 1}
                  </TableCell>
                  <TableCell
                    className="font-medium max-w-[180px] truncate"
                    title={item.description || 'No description'}
                  >
                    {item.description || 'No description'}
                  </TableCell>
                  <TableCell className="text-right">
                    {formatQuantity(expectedQty)}
                  </TableCell>
                  <TableCell className="text-right">
                    {formatQuantity(actualQty)}
                  </TableCell>
                  <TableCell className="text-center">
                    {hasDiscrepancy ? (
                      <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-yellow-100 text-yellow-800">
                        Discrepancy
                      </span>
                    ) : (
                      <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800">
                        Match
                      </span>
                    )}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
          <TableFooter>
            <TableRow>
              <TableCell />
              <TableCell className="font-semibold">Total</TableCell>
              <TableCell className="text-right font-semibold">
                {formatQuantity(totals.expectedTotal)}
              </TableCell>
              <TableCell className="text-right font-semibold">
                {formatQuantity(totals.actualTotal)}
              </TableCell>
              <TableCell className="text-center">
                {totals.actualTotal !== totals.expectedTotal ? (
                  <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-yellow-100 text-yellow-800">
                    Discrepancy
                  </span>
                ) : (
                  <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800">
                    Match
                  </span>
                )}
              </TableCell>
            </TableRow>
          </TableFooter>
        </Table>
      </div>
    </ScrollArea>
  );
}

export function ThreeWayMatchDialog({
  open,
  onOpenChange,
  invoiceId,
  lineItems,
  currency,
  associatedPOIds,
  isMatched,
}: ThreeWayMatchDialogProps) {
  const columns = useMemo((): ColumnConfig[] => {
    return [
      {
        id: 'po-lines',
        type: 'custom' as const,
        title: `Purchase Order`,
        defaultWidthPercent: 34,
        minWidthPx: 300,
        render: () => (
          <POLineItemsPanel
            poIds={associatedPOIds}
            currency={currency}
            isMatched={isMatched}
          />
        ),
      },
      {
        id: 'shipping-docs',
        type: 'custom' as const,
        title: 'Proof of Delivery',
        defaultWidthPercent: 33,
        minWidthPx: 250,
        render: () => (
          <ShippingDocsPanel poIds={associatedPOIds} currency={currency} />
        ),
      },
      {
        id: 'invoice-lines',
        type: 'custom' as const,
        title: `Invoice (${lineItems.length})`,
        defaultWidthPercent: 33,
        minWidthPx: 300,
        render: () => (
          <InvoiceLineItemsPanel
            lineItems={lineItems}
            currency={currency}
            isMatched={isMatched}
          />
        ),
      },
    ];
  }, [lineItems, currency, associatedPOIds, isMatched]);

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[9999] flex flex-col bg-background">
      <div className="flex-shrink-0 flex items-center py-3 px-3 bg-muted/20">
        <Button
          variant="ghost"
          size="sm"
          onClick={(e) => {
            e.stopPropagation();
            onOpenChange(false);
          }}
          className="gap-2"
        >
          <ArrowLeft className="h-4 w-4" />
          Back
        </Button>
      </div>

      <div className="flex-1 min-h-0">
        <DocumentComparisonProvider>
          <DocumentComparisonLayout
            columns={columns}
            className="h-full"
            onClose={() => onOpenChange(false)}
          />
        </DocumentComparisonProvider>
      </div>
    </div>
  );
}
